/*
 * OSINT Tools - Enhanced Implementation
 * 
 * Free OSINT APIs integrated without requiring API keys:
 * - IP Analysis: IP-API.com, FreeIPAPI.com (geolocation, ISP, threat indicators)
 * - Domain Analysis: Google DNS, Cloudflare DNS, DomainsDB (DNS records, registration info)
 * - URL Analysis: Basic URL parsing and domain analysis
 * - Email Analysis: Format validation, MX record checking, domain verification
 * - Phone Analysis: Format validation, country code detection, pattern analysis
 * - Username Search: Free tools like Sherlock, WhatsMyName integration
 * 
 * Premium OSINT APIs (require API keys):
 * - Social Media Search: Social Searcher, platform APIs
 * - People Search: Pipl, Spokeo, BeenVerified
 * - Background Check: Premium verification services
 * 
 * See config/osint.js for API key configuration for premium features.
 */

import React, { useState } from 'react';
import { Card, CardBody, Button, Input, Tabs, Tab, Chip, Spinner } from '@heroui/react';
import { OSINT_CONFIG, hasApiKey } from '../../config/osint';
import OSINTAnalysisModal from './OSINTAnalysisModal';
import GoogleDorkingModal from './GoogleDorkingModal';
import { 
  GlobeAltIcon, 
  ShieldCheckIcon, 
  ServerIcon, 
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ClipboardDocumentIcon,
  UserIcon,
  UsersIcon,
  IdentificationIcon,
  LockClosedIcon,
  StarIcon,
  CpuChipIcon
} from '@heroicons/react/24/outline';

const OSINTTools = () => {
  const [activeTab, setActiveTab] = useState('ip-analysis');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({});
  const [inputValue, setInputValue] = useState('');
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiModalData, setAiModalData] = useState(null);
  const [aiModalType, setAiModalType] = useState('');
  const [googleDorkingModalOpen, setGoogleDorkingModalOpen] = useState(false);
  const [googleDorkingData, setGoogleDorkingData] = useState(null);
  const [backendStatus, setBackendStatus] = useState('unknown'); // 'connected', 'disconnected', 'unknown'

  // IP Analysis using free APIs
  const analyzeIP = async (ip) => {
    setLoading(true);
    try {
      const results = {};
      
      // IP-API.com (completely free, no key required)
      try {
        const response = await fetch(`${OSINT_CONFIG.ENDPOINTS.IP_API}/${ip}?fields=status,message,continent,continentCode,country,countryCode,region,regionName,city,district,zip,lat,lon,timezone,offset,currency,isp,org,as,asname,mobile,proxy,hosting,query`);
        if (response.ok) {
          const data = await response.json();
          if (data.status === 'success') {
            results.ipapi = { ...data, source: 'IP-API.com' };
          }
        }
      } catch (e) {
        console.warn('IP-API failed:', e);
      }

      // FreeIPAPI (backup)
      try {
        const response = await fetch(`https://freeipapi.com/api/json/${ip}`);
        if (response.ok) {
          results.freeipapi = await response.json();
        }
      } catch (e) {
        console.warn('FreeIPAPI failed:', e);
      }

      // IPify (geolocation)
      try {
        const response = await fetch(`https://geo.ipify.org/api/v2/country,city?apiKey=at_XXXXXXXXXXXXXXXXXXXXXXXXXX&ipAddress=${ip}`);
        if (response.ok) {
          results.ipify = await response.json();
        }
      } catch (e) {
        console.warn('IPify failed:', e);
      }

      setResults({ ip: results });
    } catch (error) {
      console.error('IP Analysis error:', error);
      setResults({ error: 'Failed to analyze IP address' });
    } finally {
      setLoading(false);
    }
  };

  // Domain Analysis using backend API to avoid CORS issues
  const analyzeDomain = async (domain) => {
    setLoading(true);
    try {
      const response = await fetch(
        process.env.NODE_ENV === 'development' 
          ? 'http://localhost:3001/api/ai/osint/domain-analysis' 
          : '/api/ai/osint/domain-analysis',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ domain }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setResults({ domain: result.data });
        } else {
          setResults({ error: result.error || 'Domain analysis failed' });
        }
      } else {
        setResults({ error: 'Failed to analyze domain - server error' });
      }
    } catch (error) {
      console.error('Domain Analysis error:', error);
      setResults({ error: 'Failed to analyze domain - network error' });
    } finally {
      setLoading(false);
    }
  };

  // URL/Threat Analysis using backend API to avoid CORS issues
  const analyzeURL = async (url) => {
    setLoading(true);
    try {
      const response = await fetch(
        process.env.NODE_ENV === 'development' 
          ? 'http://localhost:3001/api/ai/osint/url-analysis' 
          : '/api/ai/osint/url-analysis',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setResults({ url: result.data });
        } else {
          setResults({ error: result.error || 'URL analysis failed' });
        }
      } else {
        setResults({ error: 'Failed to analyze URL - server error' });
      }
    } catch (error) {
      console.error('URL Analysis error:', error);
      setResults({ error: 'Failed to analyze URL - network error' });
    } finally {
      setLoading(false);
    }
  };

  // Email Analysis
  const analyzeEmail = async (email) => {
    setLoading(true);
    try {
      const results = {};

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const isValidFormat = emailRegex.test(email);
      
      const domain = email.split('@')[1];

      results.validation = {
        email: email,
        valid_format: isValidFormat,
        domain: domain,
        username: email.split('@')[0],
        timestamp: new Date().toISOString()
      };

      // Check if domain exists using DNS
      if (domain) {
        try {
          const response = await fetch(`${OSINT_CONFIG.ENDPOINTS.GOOGLE_DNS}?name=${domain}&type=MX`);
          if (response.ok) {
            const dnsData = await response.json();
            results.mx_records = { ...dnsData, source: 'Google DNS' };
            results.validation.has_mx_record = dnsData.Answer && dnsData.Answer.length > 0;
          }
        } catch (e) {
          console.warn('MX record check failed:', e);
        }

        // Get domain location info
        try {
          const response = await fetch(`${OSINT_CONFIG.ENDPOINTS.IP_API}/${domain}?fields=status,country,countryCode,isp,org,query`);
          if (response.ok) {
            const data = await response.json();
            if (data.status === 'success') {
              results.domain_info = { ...data, source: 'IP-API.com' };
            }
          }
        } catch (e) {
          console.warn('Domain info check failed:', e);
        }
      }

      setResults({ email: results });
    } catch (error) {
      console.error('Email Analysis error:', error);
      setResults({ error: 'Failed to analyze email' });
    } finally {
      setLoading(false);
    }
  };

  // Phone Number Analysis
  const analyzePhone = async (phone) => {
    setLoading(true);
    try {
      const results = {};

      // Clean phone number
      const cleanPhone = phone.replace(/\D/g, '');
      
      // Basic phone number validation and parsing
      results.analysis = {
        original: phone,
        cleaned: cleanPhone,
        length: cleanPhone.length,
        timestamp: new Date().toISOString()
      };

      // Basic country code detection
      const countryCodePatterns = {
        '1': 'US/Canada',
        '44': 'United Kingdom',
        '33': 'France',
        '49': 'Germany',
        '81': 'Japan',
        '86': 'China',
        '91': 'India',
        '61': 'Australia',
        '55': 'Brazil',
        '7': 'Russia',
        '39': 'Italy',
        '34': 'Spain',
        '52': 'Mexico',
        '82': 'South Korea',
        '31': 'Netherlands',
        '46': 'Sweden',
        '47': 'Norway',
        '45': 'Denmark',
        '41': 'Switzerland',
        '43': 'Austria'
      };

      // Try to identify country code
      for (const [code, country] of Object.entries(countryCodePatterns)) {
        if (cleanPhone.startsWith(code) && cleanPhone.length >= code.length + 7) {
          results.analysis.possible_country_code = code;
          results.analysis.possible_country = country;
          results.analysis.local_number = cleanPhone.substring(code.length);
          break;
        }
      }

      // Check for common formats
      results.analysis.formats = {
        is_international: phone.startsWith('+'),
        has_parentheses: phone.includes('(') && phone.includes(')'),
        has_dashes: phone.includes('-'),
        has_spaces: phone.includes(' '),
        has_dots: phone.includes('.')
      };

      // Validation checks
      results.validation = {
        min_length_valid: cleanPhone.length >= 7,
        max_length_valid: cleanPhone.length <= 15,
        contains_only_digits: /^\d+$/.test(cleanPhone),
        is_potentially_valid: cleanPhone.length >= 7 && cleanPhone.length <= 15 && /^\d+$/.test(cleanPhone)
      };

      setResults({ phone: results });
    } catch (error) {
      console.error('Phone Analysis error:', error);
      setResults({ error: 'Failed to analyze phone number' });
    } finally {
      setLoading(false);
    }
  };

  // Username Search (FREE) - Social media username enumeration using Sherlock-style execution
  const searchUsername = async (username) => {
    setLoading(true);
    try {
      console.log(`Starting real username search for: ${username}`);
      
      // Call backend API for actual Sherlock-style username enumeration
      const apiUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:3001/api/ai/osint/username-search' 
        : '/api/ai/osint/username-search';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username }),
      });

      if (!response.ok) {
        // Try to get error details from response
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorText = await response.text();
          if (errorText.includes('<!DOCTYPE') || errorText.includes('<html>')) {
            errorMessage = 'Backend API not available - receiving HTML instead of JSON. Please ensure backend server is running.';
          } else {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.error || errorMessage;
          }
        } catch (parseError) {
          // Use default error message
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Username search failed');
      }

      // Transform backend results into our expected format
      const backendData = result.data;
      const results = {
        username: backendData.username,
        execution_summary: {
          total_platforms: backendData.totalPlatforms,
          found_count: backendData.found.length,
          not_found_count: backendData.notFound.length,
          error_count: backendData.errors.length,
          tool_used: backendData.toolUsed,
          execution_time: backendData.executionTime
        },
        found_profiles: backendData.found.map(profile => ({
          platform: profile.platform,
          profile_url: profile.profileUrl,
          status: profile.status,
          checked_at: profile.checkedAt,
          confidence: 'high' // Real result, not mock
        })),
        not_found_platforms: backendData.notFound.map(platform => platform.name),
        execution_errors: backendData.errors,
        tools_executed: [
          {
            name: 'Sherlock-style Username Enumeration',
            description: 'Actual username search across major social platforms',
            platforms_checked: backendData.totalPlatforms,
            success_rate: ((backendData.totalPlatforms - backendData.errors.length) / backendData.totalPlatforms * 100).toFixed(1) + '%',
            execution_status: 'completed'
          }
        ],
        next_steps: backendData.found.length > 0 ? [
          'Review found profiles for verification',
          'Cross-reference with other intelligence sources',
          'Consider social media deep-dive analysis for found profiles'
        ] : [
          'Try variations of the username',
          'Search with additional context (name, location)',
          'Consider broader social media searches'
        ]
      };

      setResults({ username: results });
      console.log(`Username search completed: ${backendData.found.length} found, ${backendData.notFound.length} not found, ${backendData.errors.length} errors`);
    } catch (error) {
      console.error('Username search error:', error);
      setResults({ error: `Failed to search username: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  // People Search (PAID) - Comprehensive people search
  const searchPeople = async (query) => {
    setLoading(true);
    try {
      const results = {};

      // Check which paid APIs are available
      const availableAPIs = {
        pipl: hasApiKey('PIPL_KEY'),
        spokeo: hasApiKey('SPOKEO_KEY'),
        beenverified: hasApiKey('BEEN_VERIFIED_KEY')
      };

      results.api_status = availableAPIs;
      results.query = query;
      results.search_type = 'people';

      if (!Object.values(availableAPIs).some(available => available)) {
        results.error = 'No people search API keys configured';
        results.setup_required = {
          message: 'Configure API keys in your environment for people search',
          apis: [
            { name: 'Pipl', key: 'REACT_APP_PIPL_KEY', description: 'Comprehensive people search' },
            { name: 'Spokeo', key: 'REACT_APP_SPOKEO_KEY', description: 'People finder and background' },
            { name: 'BeenVerified', key: 'REACT_APP_BEEN_VERIFIED_KEY', description: 'Background checks' }
          ]
        };
      } else {
        // Simulate API calls (in real implementation, this would call actual APIs)
        results.mock_results = {
          message: 'This would search across configured people search APIs',
          query: query,
          potential_matches: [
            { name: 'Example match 1', confidence: 0.85, source: 'Mock API' },
            { name: 'Example match 2', confidence: 0.72, source: 'Mock API' }
          ],
          note: 'Actual implementation would require valid API keys and proper integration'
        };
      }

      setResults({ people: results });
    } catch (error) {
      console.error('People search error:', error);
      setResults({ error: 'Failed to search people' });
    } finally {
      setLoading(false);
    }
  };

  // Social Media Search (MIXED) - Search across social platforms with actual execution
  const searchSocialMedia = async (query) => {
    setLoading(true);
    try {
      console.log(`Starting social media search for: "${query}"`);
      
      const results = {};
      
      // Check if premium APIs are configured
      const hasSocialSearcher = hasApiKey('SOCIAL_SEARCHER_KEY');
      results.api_status = {
        social_searcher: hasSocialSearcher,
        twitter: hasApiKey('TWITTER_BEARER_TOKEN'),
        facebook: hasApiKey('FACEBOOK_ACCESS_TOKEN'),
        linkedin: hasApiKey('LINKEDIN_ACCESS_TOKEN')
      };

      // Execute free alternatives regardless of API status
      const freeToolsPromises = [];

      // 1. Execute Google Dorking
      freeToolsPromises.push(
        fetch(process.env.NODE_ENV === 'development' 
          ? 'http://localhost:3001/api/ai/osint/google-dorking' 
          : '/api/ai/osint/google-dorking', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            query: query,
            sites: ['x.com', 'facebook.com', 'instagram.com', 'linkedin.com', 'tiktok.com', 'youtube.com']
          }),
        }).then(res => res.json()).catch(err => ({ success: false, error: err.message, tool: 'google-dorking' }))
      );

      // 2. Execute Social Media Platform Check
      freeToolsPromises.push(
        fetch(process.env.NODE_ENV === 'development' 
          ? 'http://localhost:3001/api/ai/osint/social-media-check' 
          : '/api/ai/osint/social-media-check', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: query }),
        }).then(res => res.json()).catch(err => ({ success: false, error: err.message, tool: 'social-media-check' }))
      );

      // Execute all free tools in parallel
      const freeToolResults = await Promise.all(freeToolsPromises);

      // Process results
      results.query = query;
      results.search_type = 'social_media';
      results.execution_time = new Date().toISOString();
      
      // Google Dorking results
      const googleDorkingResult = freeToolResults[0];
      if (googleDorkingResult.success && googleDorkingResult.data) {
        // Handle new enhanced Google dorking format
        if (googleDorkingResult.data.tool_used && googleDorkingResult.data.tool_used.includes('Enhanced')) {
          results.google_dorking = {
            tool_used: googleDorkingResult.data.tool_used,
            total_sites: googleDorkingResult.data.total_sites || 0,
            total_results: googleDorkingResult.data.total_results || 0,
            site_results: googleDorkingResult.data.site_results || [],
            search_variations: googleDorkingResult.data.search_variations || [],
            advanced_features: googleDorkingResult.data.advanced_features || [],
            usage_tips: googleDorkingResult.data.usage_tips || [],
            execution_status: googleDorkingResult.data.execution_status || 'completed'
          };
        } else {
          // Handle legacy format
          results.google_dorking = {
            tool_used: 'Google Dorking',
            total_sites: googleDorkingResult.data.sites?.length || 0,
            total_results: googleDorkingResult.data.totalResults || 0,
            site_results: googleDorkingResult.data.results || [],
            execution_status: 'completed'
          };
        }
      } else {
        results.google_dorking = {
          tool_used: 'Google Dorking',
          execution_status: 'failed',
          error: googleDorkingResult.error || 'Unknown error occurred'
        };
      }

      // Social Media Check results
      const socialCheckResult = freeToolResults[1];
      if (socialCheckResult.success && socialCheckResult.data) {
        results.platform_checks = {
          tool_used: 'Social Media Platform Check',
          platforms_checked: socialCheckResult.data.totalPlatforms || socialCheckResult.data.total_sites || 0,
          platform_results: socialCheckResult.data.platformResults || socialCheckResult.data.site_results || [],
          execution_status: 'completed'
        };
      } else {
        results.platform_checks = {
          tool_used: 'Social Media Platform Check',
          execution_status: 'failed',
          error: socialCheckResult.error || 'Unknown error occurred'
        };
      }

      // Premium API results (if configured)
      if (hasSocialSearcher) {
        results.premium_search = {
          message: 'Social Searcher API is configured but not implemented yet',
          note: 'Integrate with Social Searcher API for advanced social media monitoring'
        };
      } else {
        results.free_alternatives_executed = {
          message: 'Executed free OSINT tools since premium APIs are not configured',
          tools_used: [
            'Google Dorking across social platforms',
            'Direct social media platform checks'
          ],
          upgrade_suggestion: 'Configure Social Searcher API key for advanced social media monitoring'
        };
      }

      // Summary
      const totalResults = (results.google_dorking?.total_results || 0);
      const platformsWithResults = results.platform_checks?.platform_results?.filter(p => p.found).length || 0;
      
      results.summary = {
        query: query,
        total_search_results: totalResults,
        platforms_with_matches: platformsWithResults,
        tools_executed: ['Google Dorking', 'Social Media Platform Check'],
        success: true,
        recommendation: totalResults > 0 || platformsWithResults > 0 
          ? 'Review found results and consider deeper analysis of specific platforms'
          : 'Try broader search terms or check spelling of names/usernames'
      };

      setResults({ social: results });
      console.log(`Social media search completed: ${totalResults} Google results, ${platformsWithResults} platform matches`);
    } catch (error) {
      console.error('Social media search error:', error);
      setResults({ error: `Failed to search social media: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  // Background Check (PAID) - Comprehensive background verification
  const runBackgroundCheck = async (query) => {
    setLoading(true);
    try {
      const results = {};

      const availableAPIs = {
        spokeo: hasApiKey('SPOKEO_KEY'),
        beenverified: hasApiKey('BEEN_VERIFIED_KEY'),
        pipl: hasApiKey('PIPL_KEY')
      };

      results.query = query;
      results.check_type = 'background';
      results.api_status = availableAPIs;

      if (!Object.values(availableAPIs).some(available => available)) {
        results.error = 'No background check APIs configured';
        results.premium_required = {
          message: 'Background checks require premium API subscriptions',
          warning: '⚠️ These services often require proper authorization for law enforcement use',
          apis: [
            { name: 'Spokeo', cost: '$19.95/month', features: ['People search', 'Background reports', 'Contact info'] },
            { name: 'BeenVerified', cost: '$26.89/month', features: ['Criminal records', 'Employment history', 'Education'] },
            { name: 'Pipl', cost: 'Custom pricing', features: ['Deep web search', 'Identity verification', 'Contact data'] }
          ]
        };
      } else {
        results.mock_check = {
          message: 'Would perform comprehensive background check using configured APIs',
          query: query,
          check_categories: [
            'Identity Verification',
            'Criminal Records',
            'Employment History',
            'Education History',
            'Contact Information',
            'Social Media Presence',
            'Property Records',
            'Court Records'
          ],
          note: 'Actual implementation requires valid API keys and proper legal authorization'
        };
      }

      setResults({ background: results });
    } catch (error) {
      console.error('Background check error:', error);
      setResults({ error: 'Failed to run background check' });
    } finally {
      setLoading(false);
    }
  };

  const handleAnalysis = () => {
    if (!inputValue.trim()) return;

    setResults({});
    
    switch (activeTab) {
      case 'ip-analysis':
        analyzeIP(inputValue.trim());
        break;
      case 'domain-analysis':
        analyzeDomain(inputValue.trim());
        break;
      case 'url-analysis':
        analyzeURL(inputValue.trim());
        break;
      case 'email-analysis':
        analyzeEmail(inputValue.trim());
        break;
      case 'phone-analysis':
        analyzePhone(inputValue.trim());
        break;
      case 'username-search':
        searchUsername(inputValue.trim());
        break;
      case 'people-search':
        searchPeople(inputValue.trim());
        break;
      case 'social-search':
        searchSocialMedia(inputValue.trim());
        break;
      case 'background-check':
        runBackgroundCheck(inputValue.trim());
        break;
      default:
        break;
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(JSON.stringify(text, null, 2));
  };

  const handleAIAnalysis = (category, data) => {
    setAiModalData(data);
    setAiModalType(category);
    setAiModalOpen(true);
  };

  const handleAnalysisComplete = (analysisResult) => {
    console.log('AI Analysis completed:', analysisResult);
    // Could store analysis results or show notifications here
  };

  // Check backend connection status
  const checkBackendStatus = async () => {
    try {
      const apiUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:3001/api/ai/osint/test' 
        : '/api/ai/osint/test';

      const response = await fetch(apiUrl, { method: 'GET' });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setBackendStatus('connected');
          return true;
        }
      }
      setBackendStatus('disconnected');
      return false;
    } catch (error) {
      console.warn('Backend connection check failed:', error);
      setBackendStatus('disconnected');
      return false;
    }
  };

  // Check backend status on component mount
  React.useEffect(() => {
    checkBackendStatus();
  }, []);

  const renderResults = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-32">
          <Spinner size="lg" />
          <span className="ml-2">Analyzing...</span>
        </div>
      );
    }

    if (results.error) {
      return (
        <Card className="border-danger">
          <CardBody>
            <div className="flex items-center gap-2 text-danger">
              <ExclamationTriangleIcon className="h-5 w-5" />
              <span>{results.error}</span>
            </div>
          </CardBody>
        </Card>
      );
    }

    if (Object.keys(results).length === 0) {
      return (
        <Card>
          <CardBody>
            <div className="text-center py-8 text-gray-500">
              <InformationCircleIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Enter a value above and click "Analyze" to start OSINT investigation</p>
            </div>
          </CardBody>
        </Card>
      );
    }

    return (
      <div className="space-y-4">
        {Object.entries(results).map(([category, data]) => (
          <Card key={category}>
            <CardBody>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold capitalize">{category} Intelligence</h3>
                <div className="flex gap-2">
                  <Chip size="sm" color="success" variant="flat">
                    {typeof data === 'object' && data !== null ? Object.keys(data).length : 0} sources
                  </Chip>
                  <Button
                    size="sm"
                    variant="light"
                    color="primary"
                    startContent={<CpuChipIcon className="h-4 w-4" />}
                    onClick={() => handleAIAnalysis(category, data)}
                  >
                    AI Analysis
                  </Button>
                  <Button
                    size="sm"
                    variant="light"
                    startContent={<ClipboardDocumentIcon className="h-4 w-4" />}
                    onClick={() => copyToClipboard(data)}
                  >
                    Copy
                  </Button>
                </div>
              </div>
              
              <div className="space-y-4">
                {typeof data === 'object' && data !== null ? Object.entries(data).map(([source, info], index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-sm uppercase tracking-wide text-gray-600 dark:text-gray-400">
                        {source.replace(/_/g, ' ')}
                      </h4>
                      <Chip size="sm" variant="dot" color={info?.status === 'success' || info?.status === undefined ? 'success' : 'danger'}>
                        {info?.status || 'active'}
                      </Chip>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800 rounded p-3">
                      {renderStructuredResult(info)}
                    </div>
                  </div>
                )) : (
                  <div className="border rounded-lg p-4">
                    <div className="bg-gray-50 dark:bg-gray-800 rounded p-3">
                      <pre className="text-xs overflow-auto whitespace-pre-wrap">
                        {JSON.stringify(data, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        ))}
      </div>
    );
  };

  const getPlaceholder = () => {
    switch (activeTab) {
      case 'ip-analysis':
        return 'Enter IP address (e.g., 8.8.8.8)';
      case 'domain-analysis':
        return 'Enter domain (e.g., example.com)';
      case 'url-analysis':
        return 'Enter URL (e.g., https://example.com)';
      case 'email-analysis':
        return 'Enter email address (e.g., user@example.com)';
      case 'phone-analysis':
        return 'Enter phone number (e.g., +1234567890)';
      case 'username-search':
        return 'Enter username (e.g., john_doe)';
      case 'people-search':
        return 'Enter name or details (e.g., John Doe)';
      case 'social-search':
        return 'Enter search term (e.g., person or topic)';
      case 'background-check':
        return 'Enter full name (e.g., John Doe)';
      default:
        return 'Enter value to analyze';
    }
  };

  const isPaidFeature = (tabKey) => {
    return ['people-search', 'background-check'].includes(tabKey);
  };

  const isMixedFeature = (tabKey) => {
    return ['social-search'].includes(tabKey);
  };

  // Render result type chip based on automation level
  const renderResultTypeChip = (info) => {
    if (info?.automated === true) {
      return <Chip size="sm" variant="flat" color="success">✓ Automated</Chip>;
    } else if (info?.automated === 'limited') {
      return <Chip size="sm" variant="flat" color="warning">~ Limited</Chip>;
    } else if (info?.requiresManualCheck === true) {
      return <Chip size="sm" variant="flat" color="default">Manual Required</Chip>;
    } else if (info?.found === true) {
      return <Chip size="sm" variant="flat" color="success">✓ Found</Chip>;
    } else if (info?.found === false) {
      return <Chip size="sm" variant="flat" color="danger">✗ Not Found</Chip>;
    }
    return null;
  };

  // Render structured result instead of raw JSON
  const renderStructuredResult = (info) => {
    // Check if this looks like real OSINT data vs raw JSON
    if (typeof info === 'object' && info !== null) {
      // Handle platform results
      if (info.platform_results || info.found_profiles || info.execution_summary) {
        return <div className="space-y-2">{renderOSINTSummary(info)}</div>;
      }
      
      // Handle individual platform checks
      if (info.platform && info.query) {
        return <div className="space-y-2">{renderPlatformCheck(info)}</div>;
      }

      // Handle tool execution results  
      if (info.tool_used && info.execution_status) {
        return <div className="space-y-2">{renderToolResult(info)}</div>;
      }
    }

    // Fallback to JSON for complex data
    return (
      <pre className="text-xs overflow-auto whitespace-pre-wrap">
        {JSON.stringify(info, null, 2)}
      </pre>
    );
  };

  const renderOSINTSummary = (info) => (
    <>
      {info.execution_summary && (
        <div className="mb-3">
          <div className="text-sm font-medium mb-1">Execution Summary</div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>Platforms: <span className="font-medium">{info.execution_summary.total_platforms}</span></div>
            <div>Found: <span className="font-medium text-green-600">{info.execution_summary.found_count}</span></div>
            <div>Success Rate: <span className="font-medium">{info.tools_executed?.[0]?.success_rate || 'N/A'}</span></div>
            <div>Tool: <span className="font-medium">{info.execution_summary.tool_used}</span></div>
          </div>
        </div>
      )}
      
      {info.found_profiles && info.found_profiles.length > 0 && (
        <div className="mb-3">
          <div className="text-sm font-medium mb-2 text-green-600">Found Profiles ({info.found_profiles.length})</div>
          {info.found_profiles.map((profile, idx) => (
            <div key={idx} className="bg-green-50 dark:bg-green-900/20 p-2 rounded text-xs mb-1">
              <div className="font-medium">{profile.platform}</div>
              <div className="text-blue-600 dark:text-blue-400">
                <a href={profile.profile_url} target="_blank" rel="noopener noreferrer">
                  {profile.profile_url}
                </a>
              </div>
            </div>
          ))}
        </div>
      )}

      {info.next_steps && (
        <div>
          <div className="text-sm font-medium mb-1">Next Steps</div>
          <ul className="text-xs space-y-1">
            {info.next_steps.map((step, idx) => (
              <li key={idx} className="flex items-start gap-1">
                <span className="text-primary">•</span>
                {step}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );

  const renderPlatformCheck = (info) => (
    <>
      <div className="flex justify-between items-center">
        <div className="text-sm font-medium">{info.platform}</div>
        <div className="flex items-center gap-2">
          {renderResultTypeChip(info)}
          {info.confidence && (
            <Chip size="sm" variant="flat" color={info.confidence === 'high' ? 'success' : info.confidence === 'medium' ? 'warning' : 'danger'}>
              {info.confidence} confidence
            </Chip>
          )}
        </div>
      </div>
      
      {info.profileUrl && (
        <div className="text-xs">
          <span className="text-gray-600">Profile URL: </span>
          <a href={info.profileUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400">
            {info.profileUrl}
          </a>
        </div>
      )}

      {info.profileData && (
        <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded text-xs">
          <div className="font-medium mb-1">Profile Data:</div>
          <div className="space-y-1">
            {info.profileData.name && <div>Name: {info.profileData.name}</div>}
            {info.profileData.bio && <div>Bio: {info.profileData.bio}</div>}
            {info.profileData.publicRepos !== undefined && <div>Repos: {info.profileData.publicRepos}</div>}
            {info.profileData.followers !== undefined && <div>Followers: {info.profileData.followers}</div>}
          </div>
        </div>
      )}

      {info.note && (
        <div className="text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 p-2 rounded">
          {info.note}
        </div>
      )}

      {info.recommendation && (
        <div className="text-xs text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 p-2 rounded">
          <span className="font-medium">Manual Check: </span>
          {info.recommendation}
        </div>
      )}
    </>
  );

  const renderToolResult = (info) => (
    <>
      <div className="flex justify-between items-center">
        <div className="text-sm font-medium flex items-center gap-2">
          {info.tool_used}
          {info.tool_used?.includes('Enhanced') && (
            <Chip size="sm" variant="flat" color="primary">🚀 Enhanced</Chip>
          )}
        </div>
        <Chip size="sm" variant="flat" color={info.execution_status === 'completed' ? 'success' : 'warning'}>
          {info.execution_status}
        </Chip>
      </div>
      
      {info.search_variations && (
        <div className="text-xs mb-2">
          <span className="font-medium">Search Variations: </span>
          <span className="text-gray-600">{info.search_variations.slice(0, 3).join(', ')}</span>
          {info.search_variations.length > 3 && <span className="text-gray-500"> +{info.search_variations.length - 3} more</span>}
        </div>
      )}
      
      {info.total_results !== undefined && (
        <div className="text-xs mb-2">
          <span className="font-medium">Coverage: </span>
          {info.total_sites} sites, {info.total_results} results
        </div>
      )}

      {info.advanced_features && (
        <div className="text-xs mb-2 bg-blue-50 dark:bg-blue-900/20 p-2 rounded">
          <div className="font-medium text-blue-700 dark:text-blue-300 mb-1">🎯 Advanced Features:</div>
          <ul className="space-y-1">
            {info.advanced_features.slice(0, 3).map((feature, idx) => (
              <li key={idx} className="flex items-start gap-1 text-blue-600 dark:text-blue-400">
                <span>•</span> {feature}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Google Dorking Modal Button */}
      {info.tool_used && info.tool_used.includes('Google Dorking') && info.site_results && (
        <div className="mb-3">
          <Button
            color="primary"
            variant="flat"
            size="md"
            startContent={<span>🎯</span>}
            onClick={() => {
              setGoogleDorkingData(info);
              setGoogleDorkingModalOpen(true);
            }}
            className="w-full"
          >
            View Detailed Google Dorking Results ({info.site_results.length} sites)
          </Button>
        </div>
      )}
      
      {info.site_results && !info.tool_used?.includes('Google Dorking') && (
        <div className="space-y-1">
          <div className="text-xs font-medium mb-2 flex items-center justify-between">
            <span>Site Results ({info.site_results.length} sites checked):</span>
            <button 
              onClick={() => {
                const allUrls = info.site_results.map(site => site.searchUrl || '').filter(url => url);
                navigator.clipboard.writeText(allUrls.join('\n'));
              }}
              className="text-blue-500 hover:text-blue-700 text-xs flex items-center gap-1"
              title="Copy all search URLs"
            >
              📋 Copy All URLs
            </button>
          </div>
          <div className="max-h-80 overflow-y-auto space-y-2">
            {info.site_results.map((site, idx) => (
              <div key={idx} className={`text-xs p-3 rounded border ${
                site.found 
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
                  : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
              }`}>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium flex items-center gap-1">
                    {site.directVerification?.found ? '✅' : site.found ? '✓' : '○'} 
                    {site.site}
                    {site.directCheckPossible && (
                      <Chip size="sm" variant="flat" color="success">Direct Check</Chip>
                    )}
                  </span>
                  <div className="flex items-center gap-1">
                    {site.confidence && (
                      <Chip size="sm" variant="flat" color={
                        site.confidence === 'high' ? 'success' : 
                        site.confidence === 'medium' ? 'warning' : 'danger'
                      }>
                        {site.confidence}
                      </Chip>
                    )}
                    <span className={`text-xs px-2 py-1 rounded ${
                      site.found 
                        ? 'bg-green-100 text-green-700 dark:bg-green-800 dark:text-green-200' 
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {site.found ? `${site.resultCount || 1} strategy${(site.resultCount || 1) > 1 ? 'ies' : 'y'}` : 'manual check'}
                    </span>
                  </div>
                </div>

                {/* Direct verification result */}
                {site.directVerification && (
                  <div className={`text-xs p-2 rounded mb-2 ${
                    site.directVerification.found 
                      ? 'bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200'
                      : 'bg-orange-100 dark:bg-orange-800 text-orange-800 dark:text-orange-200'
                  }`}>
                    <div className="font-medium">🔍 Direct Verification:</div>
                    <div>{site.directVerification.note}</div>
                    {site.directVerification.profileUrl && (
                      <a href={site.directVerification.profileUrl} target="_blank" rel="noopener noreferrer" 
                         className="text-blue-600 dark:text-blue-400 hover:underline">
                        → View Profile
                      </a>
                    )}
                  </div>
                )}

                {/* Search strategies */}
                {site.searchStrategies && site.searchStrategies.length > 0 && (
                  <div className="mb-2">
                    <div className="font-medium mb-1">🎯 Search Strategies:</div>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {site.searchStrategies.slice(0, 4).map((strategy, stratIdx) => (
                        <div key={stratIdx} className="bg-white dark:bg-gray-700 p-2 rounded border">
                          <div className="flex justify-between items-center mb-1">
                            <span className="font-medium text-xs">{strategy.type}</span>
                            <div className="flex items-center gap-1">
                              <Chip size="sm" variant="dot" color={
                                strategy.effectiveness === 'high' ? 'success' : 
                                strategy.effectiveness === 'medium' ? 'warning' : 'default'
                              }>
                                {strategy.effectiveness}
                              </Chip>
                              <button 
                                onClick={() => navigator.clipboard.writeText(strategy.searchUrl)}
                                className="text-gray-500 hover:text-gray-700 text-xs"
                                title="Copy this search URL"
                              >
                                📋
                              </button>
                            </div>
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                            {strategy.description}
                          </div>
                          <a href={strategy.searchUrl} target="_blank" rel="noopener noreferrer" 
                             className="text-blue-600 dark:text-blue-400 text-xs hover:underline flex items-center gap-1">
                            🔍 Execute Search →
                          </a>
                        </div>
                      ))}
                      {site.searchStrategies.length > 4 && (
                        <div className="text-xs text-gray-500 text-center p-1">
                          ... and {site.searchStrategies.length - 4} more strategies
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Search tips */}
                {site.searchTips && (
                  <div className="text-xs bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded">
                    <span className="font-medium text-yellow-700 dark:text-yellow-300">💡 Tip: </span>
                    <span className="text-yellow-600 dark:text-yellow-400">{site.searchTips}</span>
                  </div>
                )}

                {/* General note */}
                {site.note && !site.searchTips && (
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 italic">
                    {site.note}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {info.usage_tips && (
        <div className="text-xs mt-3 bg-blue-50 dark:bg-blue-900/20 p-2 rounded">
          <div className="font-medium text-blue-700 dark:text-blue-300 mb-1">📚 Usage Tips:</div>
          <ul className="space-y-1">
            {info.usage_tips.slice(0, 2).map((tip, idx) => (
              <li key={idx} className="flex items-start gap-1 text-blue-600 dark:text-blue-400">
                <span>•</span> {tip}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );

  return (
    <div className="h-full flex flex-col">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              OSINT Intelligence Tools
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Free open-source intelligence gathering tools for investigation and analysis
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${
                backendStatus === 'connected' ? 'bg-green-500' : 
                backendStatus === 'disconnected' ? 'bg-red-500' : 
                'bg-yellow-500'
              }`}></div>
              <span className="text-xs text-gray-600 dark:text-gray-400">
                {backendStatus === 'connected' ? 'Backend Connected' : 
                 backendStatus === 'disconnected' ? 'Backend Offline' : 
                 'Checking...'}
              </span>
            </div>
            {backendStatus === 'disconnected' && (
              <Button
                size="sm"
                variant="light"
                color="warning"
                onClick={checkBackendStatus}
              >
                Retry
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 p-6 overflow-y-auto">
        <Card>
          <CardBody>
            <div className="mb-4">
              <div className="flex items-center gap-4 mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-600 dark:text-gray-400">Free Tools</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span className="text-sm text-gray-600 dark:text-gray-400">Mixed (Free + Paid)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                  <span className="text-sm text-gray-600 dark:text-gray-400">Paid APIs Required</span>
                </div>
              </div>
            </div>

            <Tabs 
              selectedKey={activeTab} 
              onSelectionChange={setActiveTab}
              variant="underlined"
              className="mb-6"
            >
              {/* FREE TOOLS */}
              <Tab 
                key="ip-analysis" 
                title={
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <ServerIcon className="h-4 w-4" />
                    IP Analysis
                  </div>
                }
              />
              <Tab 
                key="domain-analysis" 
                title={
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <GlobeAltIcon className="h-4 w-4" />
                    Domain Analysis
                  </div>
                }
              />
              <Tab 
                key="url-analysis" 
                title={
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <ShieldCheckIcon className="h-4 w-4" />
                    URL/Threat Analysis
                  </div>
                }
              />
              <Tab 
                key="email-analysis" 
                title={
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <InformationCircleIcon className="h-4 w-4" />
                    Email Analysis
                  </div>
                }
              />
              <Tab 
                key="phone-analysis" 
                title={
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <MagnifyingGlassIcon className="h-4 w-4" />
                    Phone Analysis
                  </div>
                }
              />
              <Tab 
                key="username-search" 
                title={
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <UserIcon className="h-4 w-4" />
                    Username Search
                  </div>
                }
              />
              
              {/* MIXED TOOLS */}
              <Tab 
                key="social-search" 
                title={
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <UsersIcon className="h-4 w-4" />
                    Social Media
                  </div>
                }
              />
              
              {/* PAID TOOLS */}
              <Tab 
                key="people-search" 
                title={
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                    <IdentificationIcon className="h-4 w-4" />
                    People Search
                    <StarIcon className="h-3 w-3 text-orange-500" />
                  </div>
                }
              />
              <Tab 
                key="background-check" 
                title={
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                    <LockClosedIcon className="h-4 w-4" />
                    Background Check
                    <StarIcon className="h-3 w-3 text-orange-500" />
                  </div>
                }
              />
            </Tabs>

            <div className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder={getPlaceholder()}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAnalysis()}
                  className="flex-1"
                />
                <Button 
                  onClick={handleAnalysis}
                  isLoading={loading}
                  color={isPaidFeature(activeTab) ? "warning" : isMixedFeature(activeTab) ? "primary" : "success"}
                  startContent={!loading && <MagnifyingGlassIcon className="h-4 w-4" />}
                >
                  {loading ? 'Analyzing...' : 'Analyze'}
                </Button>
              </div>

              {isPaidFeature(activeTab) && (
                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-orange-700 dark:text-orange-300">
                    <StarIcon className="h-4 w-4" />
                    <span className="text-sm font-medium">Premium API Required</span>
                  </div>
                  <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                    This feature requires paid API subscriptions. Configure API keys in your environment.
                  </p>
                </div>
              )}

              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {activeTab === 'ip-analysis' && (
                    <>
                      <Chip size="sm" variant="flat">Geolocation</Chip>
                      <Chip size="sm" variant="flat">ISP Info</Chip>
                      <Chip size="sm" variant="flat">Timezone</Chip>
                      <Chip size="sm" variant="flat">Threat Status</Chip>
                    </>
                  )}
                  {activeTab === 'domain-analysis' && (
                    <>
                      <Chip size="sm" variant="flat">DNS Records</Chip>
                      <Chip size="sm" variant="flat">WHOIS Data</Chip>
                      <Chip size="sm" variant="flat">Registration Info</Chip>
                      <Chip size="sm" variant="flat">Name Servers</Chip>
                    </>
                  )}
                  {activeTab === 'url-analysis' && (
                    <>
                      <Chip size="sm" variant="flat">Security Scan</Chip>
                      <Chip size="sm" variant="flat">Screenshot</Chip>
                      <Chip size="sm" variant="flat">Technology Stack</Chip>
                      <Chip size="sm" variant="flat">Threat Assessment</Chip>
                    </>
                  )}
                  {activeTab === 'email-analysis' && (
                    <>
                      <Chip size="sm" variant="flat">Deliverability</Chip>
                      <Chip size="sm" variant="flat">Domain Status</Chip>
                      <Chip size="sm" variant="flat">MX Records</Chip>
                      <Chip size="sm" variant="flat">Breach Check</Chip>
                    </>
                  )}
                  {activeTab === 'phone-analysis' && (
                    <>
                      <Chip size="sm" variant="flat">Validation</Chip>
                      <Chip size="sm" variant="flat">Carrier Info</Chip>
                      <Chip size="sm" variant="flat">Location</Chip>
                      <Chip size="sm" variant="flat">Line Type</Chip>
                    </>
                  )}
                  {activeTab === 'username-search' && (
                    <>
                      <Chip size="sm" variant="flat" color="success">✓ Sherlock-style Search</Chip>
                      <Chip size="sm" variant="flat" color="success">✓ Live Platform Checks</Chip>
                      <Chip size="sm" variant="flat" color="success">✓ Real-time Results</Chip>
                      <Chip size="sm" variant="flat" color="default">Rate Limited</Chip>
                    </>
                  )}
                  {activeTab === 'social-search' && (
                    <>
                      <Chip size="sm" variant="flat" color="success">✓ Google Dorking</Chip>
                      <Chip size="sm" variant="flat" color="success">✓ Platform Search</Chip>
                      <Chip size="sm" variant="flat" color="primary">Social Searcher API</Chip>
                      <Chip size="sm" variant="flat" color="default">Multiple Tools</Chip>
                    </>
                  )}
                  {activeTab === 'people-search' && (
                    <>
                      <Chip size="sm" variant="flat" color="warning">Pipl</Chip>
                      <Chip size="sm" variant="flat" color="warning">Spokeo</Chip>
                      <Chip size="sm" variant="flat" color="warning">BeenVerified</Chip>
                      <Chip size="sm" variant="flat" color="warning">TruePeopleSearch</Chip>
                    </>
                  )}
                  {activeTab === 'background-check' && (
                    <>
                      <Chip size="sm" variant="flat" color="warning">Criminal Records</Chip>
                      <Chip size="sm" variant="flat" color="warning">Employment History</Chip>
                      <Chip size="sm" variant="flat" color="warning">Property Records</Chip>
                      <Chip size="sm" variant="flat" color="warning">Court Records</Chip>
                    </>
                  )}
                </div>

                {/* Example values for testing */}
                <div className="border-t pt-3">
                  <p className="text-xs text-gray-500 mb-2">Try these examples:</p>
                  <div className="flex flex-wrap gap-2">
                    {activeTab === 'ip-analysis' && (
                      <>
                        <Button size="sm" variant="flat" onClick={() => setInputValue('8.8.8.8')}>Google DNS</Button>
                        <Button size="sm" variant="flat" onClick={() => setInputValue('1.1.1.1')}>Cloudflare</Button>
                        <Button size="sm" variant="flat" onClick={() => setInputValue('208.67.222.222')}>OpenDNS</Button>
                      </>
                    )}
                    {activeTab === 'domain-analysis' && (
                      <>
                        <Button size="sm" variant="flat" onClick={() => setInputValue('google.com')}>google.com</Button>
                        <Button size="sm" variant="flat" onClick={() => setInputValue('github.com')}>github.com</Button>
                        <Button size="sm" variant="flat" onClick={() => setInputValue('cloudflare.com')}>cloudflare.com</Button>
                      </>
                    )}
                    {activeTab === 'url-analysis' && (
                      <>
                        <Button size="sm" variant="flat" onClick={() => setInputValue('https://google.com')}>Google</Button>
                        <Button size="sm" variant="flat" onClick={() => setInputValue('https://github.com')}>GitHub</Button>
                        <Button size="sm" variant="flat" onClick={() => setInputValue('https://example.com')}>Example</Button>
                      </>
                    )}
                    {activeTab === 'email-analysis' && (
                      <>
                        <Button size="sm" variant="flat" onClick={() => setInputValue('test@gmail.com')}>Gmail</Button>
                        <Button size="sm" variant="flat" onClick={() => setInputValue('user@example.com')}>Example</Button>
                        <Button size="sm" variant="flat" onClick={() => setInputValue('info@github.com')}>GitHub</Button>
                      </>
                    )}
                    {activeTab === 'phone-analysis' && (
                      <>
                        <Button size="sm" variant="flat" onClick={() => setInputValue('+1-555-123-4567')}>US Format</Button>
                        <Button size="sm" variant="flat" onClick={() => setInputValue('+44-20-7946-0958')}>UK Format</Button>
                        <Button size="sm" variant="flat" onClick={() => setInputValue('(555) 123-4567')}>Local Format</Button>
                      </>
                    )}
                    {activeTab === 'username-search' && (
                      <>
                        <Button size="sm" variant="flat" onClick={() => setInputValue('john_doe')}>john_doe</Button>
                        <Button size="sm" variant="flat" onClick={() => setInputValue('detective123')}>detective123</Button>
                        <Button size="sm" variant="flat" onClick={() => setInputValue('user2024')}>user2024</Button>
                      </>
                    )}
                    {activeTab === 'social-search' && (
                      <>
                        <Button size="sm" variant="flat" onClick={() => setInputValue('John Smith')}>Person Name</Button>
                        <Button size="sm" variant="flat" onClick={() => setInputValue('cybercrime investigation')}>Topic</Button>
                        <Button size="sm" variant="flat" onClick={() => setInputValue('@username')}>Social Handle</Button>
                      </>
                    )}
                    {activeTab === 'people-search' && (
                      <>
                        <Button size="sm" variant="flat" onClick={() => setInputValue('John Smith')}>John Smith</Button>
                        <Button size="sm" variant="flat" onClick={() => setInputValue('Jane Doe Los Angeles')}>Jane Doe + City</Button>
                        <Button size="sm" variant="flat" onClick={() => setInputValue('Michael Johnson 1985')}>Name + Birth Year</Button>
                      </>
                    )}
                    {activeTab === 'background-check' && (
                      <>
                        <Button size="sm" variant="flat" onClick={() => setInputValue('John Smith')}>John Smith</Button>
                        <Button size="sm" variant="flat" onClick={() => setInputValue('Jane Elizabeth Doe')}>Full Name</Button>
                        <Button size="sm" variant="flat" onClick={() => setInputValue('Robert Johnson Jr')}>With Suffix</Button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {renderResults()}
            </div>
          </CardBody>
        </Card>
      </div>

      {/* AI Analysis Modal */}
      <OSINTAnalysisModal
        isOpen={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
        osintType={aiModalType}
        osintData={aiModalData}
        onAnalysisComplete={handleAnalysisComplete}
      />

      {/* Google Dorking Modal */}
      <GoogleDorkingModal
        isOpen={googleDorkingModalOpen}
        onClose={() => setGoogleDorkingModalOpen(false)}
        dorkingData={googleDorkingData}
      />
    </div>
  );
};

export default OSINTTools; 